/**
 * Habita Advisor — client SDK (fire-and-forget).
 *
 * Reglas duras:
 *   1. NUNCA bloquea la UI. Todos los errores son silenciosos.
 *   2. Timeout corto (1200ms) + AbortController.
 *   3. Un Context activo por sesión (sessionStorage). Al cerrar la pestaña
 *      la sesión se pierde — no seguimos al usuario entre pestañas ni días.
 *   4. Si el backend responde 404 (feature flag OFF), el cliente entra
 *      en modo no-op para toda la sesión.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${BACKEND_URL}/api/advisor`;
const TIMEOUT_MS = 1200;
const REC_TIMEOUT_MS = 2200;
const STORAGE_KEY = 'habita_advisor_ctx';
const SESSION_KEY = 'habita_advisor_session';

// In-memory kill switch: once we detect the Advisor is off (404) o el
// entorno no está disponible, dejamos de hacer llamadas durante la sesión.
let _disabled = false;

function _sessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `sess-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch { return null; }
}

function _readCtx() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function _writeCtx(ctx) {
  try {
    if (ctx) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
    else sessionStorage.removeItem(STORAGE_KEY);
    // Notifica a componentes que dependen del Context (ej: AdvisorNavLink en Header)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('habita:context:updated', {
        detail: { id: ctx?.id || null },
      }));
    }
  } catch { /* ignore */ }
}

async function _fetch(path, opts = {}) {
  if (_disabled) return null;
  const ctrl = new AbortController();
  const timeout = opts.timeoutMs || TIMEOUT_MS;
  const to = setTimeout(() => ctrl.abort(), timeout);
  // Adjunta JWT si el usuario está autenticado — habilita endpoints que
  // requieren identidad (ej. /context/mine/latest para ACT cross-session).
  const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
  try {
    const token = localStorage.getItem('habita_token');
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch { /* no localStorage */ }
  try {
    const r = await fetch(`${BASE}${path}`, {
      method: opts.method || 'POST',
      headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      signal: ctrl.signal,
      credentials: 'omit',
    });
    if (r.status === 404) { _disabled = true; return null; }
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
  finally { clearTimeout(to); }
}

/**
 * Devuelve el Context activo (si existe y sigue vivo) o crea uno nuevo.
 *
 * Cross-session ACT (Fase 5.2): si no hay ctx local pero el usuario está
 * autenticado y tiene un ctx reciente en backend, lo restaura antes de
 * crear uno nuevo.
 *
 * `entryPoint` sólo se usa cuando no hay Context activo ni memoria previa.
 */
export async function getOrStartContext({ entryPoint = 'other', intent = null, payload = {} } = {}) {
  if (_disabled) return null;
  const existing = _readCtx();
  if (existing?.id) return existing;
  // Intento 1: restaurar ctx cross-session del usuario autenticado.
  const restored = await _fetch('/context/mine/latest', { method: 'GET' });
  if (restored?.context_id) {
    const ctxLike = {
      id: restored.context_id,
      entry_point: restored.entry_point,
      intent: restored.intent,
      payload: restored.payload,
      restored: true,
      stale_days: restored.stale_days ?? null,
    };
    _writeCtx(ctxLike);
    return ctxLike;
  }
  // Intento 2: crear uno nuevo.
  const ctx = await _fetch('/context', {
    method: 'POST',
    body: { session_id: _sessionId(), entry_point: entryPoint, intent, payload },
  });
  if (ctx?.id) _writeCtx(ctx);
  return ctx;
}

/**
 * Aplica un patch al Context activo. Si no hay Context activo, no hace nada.
 * `eventKind` es un tag semántico (ej: 'filter_applied', 'property_viewed').
 */
export async function updateContext(eventKind, payloadPatch = {}) {
  if (_disabled) return null;
  const cur = _readCtx();
  if (!cur?.id) return null;
  const ctx = await _fetch(`/context/${cur.id}`, {
    method: 'PATCH',
    body: { event_kind: eventKind, payload_patch: payloadPatch },
  });
  if (ctx?.id) _writeCtx(ctx);
  return ctx;
}

/**
 * Cierra el Context actual como COMPLETED (ej: el usuario contactó a un agente).
 * Después de esto, la próxima búsqueda abre un Context nuevo.
 */
export async function completeContext(reason) {
  if (_disabled) return null;
  const cur = _readCtx();
  if (!cur?.id) return null;
  const ctx = await _fetch(`/context/${cur.id}/complete`, {
    method: 'POST', body: { reason: reason || null },
  });
  _writeCtx(null);
  return ctx;
}

export function getActiveContextId() {
  return _readCtx()?.id || null;
}

export function isDisabled() { return _disabled; }

/**
 * Pide una recomendación para el Context activo. Devuelve null si el
 * Advisor está apagado, no hay Context, o si tarda más de REC_TIMEOUT_MS.
 * Silencioso ante errores.
 */
export async function recommend({ limit = 3, targetPropertyId = null } = {}) {
  if (_disabled) return null;
  const cur = _readCtx();
  if (!cur?.id) return null;
  const body = { context_id: cur.id, limit };
  if (targetPropertyId) body.exclude_ids = []; // reservamos para lógica futura
  return _fetch('/recommendations', { method: 'POST', body, timeoutMs: REC_TIMEOUT_MS });
}

/**
 * Registra un evento sobre una recomendación (fire-and-forget).
 * event: 'shown' | 'viewed' | 'explanation_opened' | 'clicked' | 'dismissed' | 'contacted'
 */
export async function recordRecommendationEvent(recId, event, metadata = null) {
  if (_disabled || !recId) return null;
  return _fetch(`/recommendations/${recId}/events`, {
    method: 'POST', body: { event, metadata },
  });
}

// ────────────────────────────────────────────────────────────────────
// Release 0.6 · Decision Companion
// Prompts deterministas que resuelven incertidumbres sin chat ni IA.
// ────────────────────────────────────────────────────────────────────

/**
 * Devuelve prompts activos + Confidence Graph resumen para el Context actual.
 * Silencioso si el Advisor está apagado o no hay Context.
 */
export async function getCompanionPrompts() {
  if (_disabled) return null;
  const cur = _readCtx();
  if (!cur?.id) return null;
  const url = `/companion/prompts?context_id=${encodeURIComponent(cur.id)}`;
  const ctrl = new AbortController();
  const to = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const r = await fetch(`${BASE}${url}`, {
      method: 'GET',
      signal: ctrl.signal,
      credentials: 'omit',
    });
    if (r.status === 404) { _disabled = true; return null; }
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
  finally { clearTimeout(to); }
}

export async function markCompanionShown(promptId) {
  if (_disabled || !promptId) return null;
  return _fetch(`/companion/prompts/${promptId}/shown`, { method: 'POST', body: {} });
}

export async function resolveCompanionPrompt(promptId, optionValue) {
  if (_disabled || !promptId) return null;
  return _fetch(`/companion/prompts/${promptId}/resolve`, {
    method: 'POST', body: { option_value: optionValue },
  });
}

export async function dismissCompanionPrompt(promptId) {
  if (_disabled || !promptId) return null;
  return _fetch(`/companion/prompts/${promptId}/dismiss`, { method: 'POST', body: {} });
}

// ────────────────────────────────────────────────────────────────────
// Release 0.7 · Decision Quality
// El motor NO decide por el usuario. Solo evalúa el estado del proceso.
// ────────────────────────────────────────────────────────────────────

export async function getDecisionQuality({ recordView = true } = {}) {
  if (_disabled) return null;
  const cur = _readCtx();
  if (!cur?.id) return null;
  const url = `/quality?context_id=${encodeURIComponent(cur.id)}&record_view=${recordView}`;
  const ctrl = new AbortController();
  const to = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const r = await fetch(`${BASE}${url}`, {
      method: 'GET', signal: ctrl.signal, credentials: 'omit',
    });
    if (r.status === 404) { _disabled = true; return null; }
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
  finally { clearTimeout(to); }
}

export async function recordInsightOpened(insightId) {
  if (_disabled || !insightId) return null;
  const cur = _readCtx();
  if (!cur?.id) return null;
  return _fetch(
    `/quality/insights/opened?context_id=${encodeURIComponent(cur.id)}`,
    { method: 'POST', body: { insight_id: insightId } },
  );
}

export async function recordDecisionCompleted({ note = '' } = {}) {
  if (_disabled) return null;
  const cur = _readCtx();
  if (!cur?.id) return null;
  return _fetch('/quality/complete', {
    method: 'POST',
    body: { context_id: cur.id, note: note.slice(0, 200) },
  });
}

// ────────────────────────────────────────────────────────────────────
// Release 0.9 Fase 1 · Confidence Graph — escritura directa (guided start)
// ────────────────────────────────────────────────────────────────────

export async function setConfidenceAttribute(key, value) {
  if (_disabled || !key) return null;
  const cur = _readCtx();
  if (!cur?.id) return null;
  return _fetch('/confidence/attribute', {
    method: 'POST',
    body: { context_id: cur.id, key, value },
  });
}

export async function getConfidenceGraph() {
  if (_disabled) return null;
  const cur = _readCtx();
  if (!cur?.id) return null;
  const ctrl = new AbortController();
  const to = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const r = await fetch(
      `${BASE}/confidence/graph?context_id=${encodeURIComponent(cur.id)}`,
      { method: 'GET', signal: ctrl.signal, credentials: 'omit' },
    );
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
  finally { clearTimeout(to); }
}


// ────────────────────────────────────────────────────────────────────
// Release 0.9 Fase B · Telemetría del flujo conversacional
// ────────────────────────────────────────────────────────────────────

/**
 * Emite un evento de telemetría del guided start. Fire-and-forget:
 *   - No espera respuesta (no bloquea nunca la UX).
 *   - Silencia todos los errores.
 *   - `dwell_ms` / `session_ms` deben venir del caller — el SDK no mide tiempo.
 *
 * Usa navigator.sendBeacon cuando esté disponible (ideal para `beforeunload`).
 */
export function recordGuidedEvent(event, meta = {}) {
  if (_disabled || !event) return;
  const cur = _readCtx();
  const body = {
    context_id: cur?.id || null,
    event,
    turn_key: meta.turnKey || null,
    turn_index: typeof meta.turnIndex === 'number' ? meta.turnIndex : null,
    dwell_ms: typeof meta.dwellMs === 'number' ? Math.round(meta.dwellMs) : null,
    session_ms: typeof meta.sessionMs === 'number' ? Math.round(meta.sessionMs) : null,
    metadata: meta.extra && typeof meta.extra === 'object' ? meta.extra : null,
  };
  const url = `${BASE}/guided/events`;
  const json = JSON.stringify(body);
  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([json], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch { /* fallthrough to fetch */ }
  // Fallback: fetch con keepalive para no perder eventos en unload
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      credentials: 'omit',
      keepalive: true,
    }).catch(() => {});
  } catch { /* silent */ }
}

export const advisor = {
  getOrStartContext,
  updateContext,
  completeContext,
  getActiveContextId,
  isDisabled,
  recommend,
  recordRecommendationEvent,
  // R0.6 · Companion
  getCompanionPrompts,
  markCompanionShown,
  resolveCompanionPrompt,
  dismissCompanionPrompt,
  // R0.7 · Decision Quality
  getDecisionQuality,
  recordInsightOpened,
  recordDecisionCompleted,
  // R0.9 · Guided Start
  setConfidenceAttribute,
  getConfidenceGraph,
  recordGuidedEvent,
};

export default advisor;
